import { useCopilot } from '../../api/copilot';
import { callSafely } from './util';

/**
 * A line from the app itself rather than from either side of the conversation:
 * a handoff between agents, a notice ("Mic paused"), or an error.
 *
 * Small, centred and muted, so it reads as a margin note on the thread and
 * never as something the agent said. Errors take the error colour and a
 * leading mark, because in the two-colour theme `--red` can be ink and colour
 * alone would not tell them apart.
 *
 * A line may carry one action (`card.actionId`): `reconnect` after the
 * connection gave up or the budget ran out, `mic` after the mic was paused for
 * silence. The button is on the line because that is where the reader just
 * learned why they need it.
 */
export default function SystemLine({ message }) {
  const {
    reconnect, setMic, unlockAudio, available = false, listening = false,
  } = useCopilot();
  if (!message) return null;
  const kind = message.kind || 'notice';
  const error = kind === 'error' || message.ok === false;
  const action = message.card?.actionId;

  return (
    <div className={`cp-sys k-${kind}${error ? ' err' : ''}`}>
      {error && <span className="cp-sys-mark" aria-hidden="true">!</span>}
      {kind === 'handoff' && <span className="cp-sys-mark" aria-hidden="true">⇢</span>}
      <span className="cp-sys-text">{message.text || message.card?.title}</span>
      {action === 'reconnect' && (
        <button type="button" className="cp-link-btn" onClick={() => callSafely(reconnect)}>
          Reconnect
        </button>
      )}
      {/* `setMic(true)` rather than a toggle: pressing this after the mic was
          already turned back on some other way must not switch it off again. */}
      {action === 'mic' && !listening && (
        <button
          type="button"
          className="cp-link-btn"
          disabled={!available}
          title={available ? 'Turn the microphone back on' : 'The assistant is not connected'}
          onClick={() => {
            callSafely(unlockAudio);
            callSafely(setMic, true);
          }}
        >
          Talk
        </button>
      )}
    </div>
  );
}
